import { useState } from 'react';
import { useIntl } from 'react-intl';
import useAccountFormState from '../../hooks/useAccountFormState';
import { logGRError, refreshTokenCall, resendVerifyCode } from '../../../utils/guidedUtil';
import { verifyToken } from '../../../api/apiRequest';
import { API_STATUS, ERROR_CODES, SMS, SUCCESS_CODES } from '../../../constants';
import { STORAGE_CONFIG } from '../../../../../constants/storageConfig';
import { useAnalyticsContext } from '../../../../../config/GoogleTagManagerEvents';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../../../constants/analyticsConstants/Ecommerce';

const useNumberVerification = () => {
    const [failedModal, setFailedModal] = useState(false);
    const [timeoutModal, setTimeoutModal] = useState(false);
    const [formStarted, setFormStarted] = useState(false);
    const [showOtpSendBanner, setShowOtpSendBanner] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { createCashAccount } = useAccountFormState();
    const intl = useIntl();
    const {
        sendEventsForEmailVerifyFormStarted,
        sendEventsForEmailVerifyFormError,
        sendEventsForEmailVerifyFormCompleted,
    } = useAnalyticsContext();

    const handleResendCode = async mobNo => {
        try {
            setShowOtpSendBanner(false);
            setIsLoading(true);
            const response = await resendVerifyCode(mobNo, SMS);
            if (response.status === SUCCESS_CODES.SUCCESS_CODE_201) {
                setShowOtpSendBanner(true);
            } else if (response.error) {
                const {
                    error: {
                        response: { status }
                    }
                } = response;
                if (status === ERROR_CODES.TOO_MANY_REQUESTS) {
                   //show verification failed modal
                   setFailedModal(true);
                } else {
                    throw response.error;
                }
            }
            setIsLoading(false);
        } catch (error) {
            logGRError(error);
            setIsLoading(false);
        }
    };

    const validateVerificationCode = async (mobNo, otp) => {
        try {
            await refreshTokenCall();
            const response = await verifyToken(mobNo, otp);
            const {
                data: { data: { status: status = '' } = {} } = {},
                error: { response: { status: errorStatus = '' } = {} } = {}
            } = response || {};

            switch (status) {
                case API_STATUS.APPROVED:
                    // call customer api again
                    sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.USER_PHONE_VERIFIED, true);
                    sendEventsForEmailVerifyFormCompleted(
                        EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_COMPLETED,
                        EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_JOIN_CASH_FORM_NAME,
                        EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_PERSONAL
                    );
                    await createCashAccount();
                    break;

                case API_STATUS.PENDING:
                    setErrorMessage(intl.formatMessage({ id: 'invalid_code' }));
                    sendEventsForEmailVerifyFormError(
                        EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR,
                        EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_JOIN_CASH_FORM_NAME,
                        EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR_INCORRECT_OTP,
                        EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_PERSONAL
                    );
                    setIsLoading(false);
                    break;
                default:
                    if (errorStatus === ERROR_CODES.NOT_FOUND) {
                        //verification timeout modal
                        setTimeoutModal(true);
                        sendEventsForEmailVerifyFormError(
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR,
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_JOIN_CASH_FORM_NAME,
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR_TIMEOUT,
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_PERSONAL
                        );
                    } else if (errorStatus === ERROR_CODES.TOO_MANY_REQUESTS) {
                        //verification failed modal
                        setFailedModal(true);
                        sendEventsForEmailVerifyFormError(
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR,
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_JOIN_CASH_FORM_NAME,
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR_TOO_MANY_REQUESTS,
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_PERSONAL
                        );
                    } else {
                        throw response.error;
                    }
                    setIsLoading(false);
                    break;
            }
        } catch (error) {
            logGRError(error);
            setIsLoading(false);
        }
    };

    const onInputFieldFocus = () => {
        if (!formStarted) {
            sendEventsForEmailVerifyFormStarted(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_STARTED,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_JOIN_CASH_FORM_NAME,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_PERSONAL
            );
            setFormStarted(true);
        }
        setShowOtpSendBanner(false);
        setErrorMessage('');
    };

    return {
        failedModal,
        timeoutModal,
        setFailedModal,
        setTimeoutModal,
        showOtpSendBanner,
        validateVerificationCode,
        isLoading,
        setIsLoading,
        setErrorMessage,
        handleResendCode,
        errorMessage,
        onInputFieldFocus
    };
};

export default useNumberVerification;
